import { useCallback, useEffect, useRef } from 'react';

const MENU_ITEM_SELECTOR = [
  '[role="menuitem"]',
  '[role="menuitemradio"]',
  '[role="menuitemcheckbox"]',
].join(',');

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

interface OverlayFocusOptions {
  /**
   * Menus move focus between `[role="menuitem"]` children with the arrow keys
   * and wrap at both ends, per the WAI-ARIA menu pattern.
   */
  menu?: boolean;
  /** Focus this element on open instead of the first focusable descendant. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /**
   * Focus the container itself on open. Use when the first focusable control
   * can disappear while the overlay is open — a button that unmounts under
   * focus drops it to `document.body`, outside the trap.
   */
  focusContainer?: boolean;
}

/**
 * Focus management for the shell's custom overlays.
 *
 * These overlays are hand-built rather than MUI `Menu`/`Dialog` because the
 * design fixes their padding, radii and shadows exactly — but MUI is also what
 * normally provides the focus trap, initial focus and focus restoration. This
 * hook supplies those without touching the visuals.
 */
export const useOverlayFocus = <T extends HTMLElement>(
  open: boolean,
  { menu = false, initialFocusRef, focusContainer = false }: OverlayFocusOptions = {},
) => {
  const containerRef = useRef<T | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const collect = useCallback((selector: string): HTMLElement[] => {
    const container = containerRef.current;
    if (!container) return [];
    return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
      (element) =>
        // A disabled row (an unavailable language, say) stays visible for the
        // sake of the dependency but must not swallow arrow-key focus.
        !element.hasAttribute('disabled') &&
        element.getAttribute('aria-disabled') !== 'true' &&
        (element.offsetParent !== null || element === document.activeElement),
    );
  }, []);

  /**
   * Tab cycles every focusable descendant. Arrow keys walk only the menu items.
   * Conflating the two would strand any non-menuitem control in a menu — the
   * profile menu's Copy affordance, for one — with no way to reach it.
   */
  const tabbableItems = useCallback(() => collect(FOCUSABLE_SELECTOR), [collect]);
  const menuItems = useCallback(() => collect(MENU_ITEM_SELECTOR), [collect]);

  useEffect(() => {
    if (!open) return undefined;
    // Captured before focus moves, so it survives the overlay closing.
    triggerRef.current = document.activeElement as HTMLElement | null;

    const target = focusContainer
      ? containerRef.current
      : (initialFocusRef?.current ??
        (menu ? menuItems()[0] : tabbableItems()[0]) ??
        containerRef.current);
    target?.focus();

    return () => {
      const trigger = triggerRef.current;
      // The trigger can be gone if the overlay navigated away from the page.
      if (trigger && document.contains(trigger)) {
        trigger.focus();
      }
    };
  }, [open, initialFocusRef, focusContainer, menu, menuItems, tabbableItems]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const active = document.activeElement as HTMLElement | null;

      if (event.key === 'Tab') {
        const items = tabbableItems();
        if (items.length === 0) return;
        // Without this, Tab walks out of the overlay into the page behind it.
        event.preventDefault();
        const index = active ? items.indexOf(active) : -1;
        if (index === -1) {
          // Focus is on the container itself (the notifications panel opens
          // that way). The wrap formula would compute length - 2 for Shift+Tab;
          // entering from outside the list means first or last, nothing else.
          items[event.shiftKey ? items.length - 1 : 0].focus();
          return;
        }
        const step = event.shiftKey ? -1 : 1;
        items[(index + step + items.length) % items.length].focus();
        return;
      }

      if (!menu) return;

      const items = menuItems();
      if (items.length === 0) return;
      const index = active ? items.indexOf(active) : -1;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = index === -1 ? 0 : (index + step + items.length) % items.length;
        items[next].focus();
      } else if (event.key === 'Home') {
        event.preventDefault();
        items[0].focus();
      } else if (event.key === 'End') {
        event.preventDefault();
        items[items.length - 1].focus();
      }
    },
    [tabbableItems, menuItems, menu],
  );

  return { containerRef, onKeyDown };
};
